/**
 * Financial Period Detail Panel — slide-over with table-card layout matching
 * the main worksheet style. Inline editing, source breakdown, date pickers,
 * and change history.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "@cvx/_generated/api";
import { format as fmtDate, parse, isValid } from "date-fns";
import {
  X,
  FileText,
  Pencil,
  Check,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  Calculator,
  Link2,
  ArrowRightLeft,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { displayPeriodLabel } from "./granularity";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Badge } from "@/ui/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import {
  FINANCIAL_PERIOD_FIELD_FORMATTERS,
  FINANCIAL_PERIOD_FIELD_LABELS,
} from "./historyFormatters";
import type { FinancialPeriodEntryUI, FieldSourceUI } from "./types";

/**
 * `updateFinancialPeriodField` still declares `value: v.any()` since the
 * mutation accepts whatever type the target period field holds. Every call
 * site in this panel only ever saves a label/date string, a numeric metric,
 * or `undefined` (to clear a field) — that's what's typed at this boundary.
 */
type FieldSaveValue = string | number | undefined;

// ---------------------------------------------------------------------------
// Bold total fields — match the main table styling
// ---------------------------------------------------------------------------

const BOLD_FIELDS = new Set([
  "totalCurrentAssets",
  "totalNonCurrentAssets",
  "totalAssets",
  "totalCurrentLiabilities",
  "totalNonCurrentLiabilities",
  "totalLiabilities",
  "netAssets",
  "totalEquity",
  "netProfitAfterTax",
  "grossProfit",
  "workingCapital",
  "netCashFlow",
]);

// ---------------------------------------------------------------------------
// Field config
// ---------------------------------------------------------------------------

interface FieldDef {
  label: string;
  field: string;
  format: "currency" | "ratio" | "percentage";
  derived?: boolean;
  formula?: string;
}

const FIELD_CATEGORIES: Array<{ title: string; fields: FieldDef[] }> = [
  {
    title: "Income Statement",
    fields: [
      { label: "Revenue", field: "revenue", format: "currency" },
      {
        label: "Cost of Goods Sold",
        field: "costOfGoodsSold",
        format: "currency",
      },
      { label: "Gross Profit", field: "grossProfit", format: "currency" },
      {
        label: "Operating Expenses",
        field: "operatingExpenses",
        format: "currency",
      },
      { label: "EBITDA", field: "ebitda", format: "currency" },
      { label: "D&A", field: "depreciationAmortisation", format: "currency" },
      { label: "EBIT", field: "ebit", format: "currency" },
      {
        label: "Interest Expense",
        field: "interestExpense",
        format: "currency",
      },
      {
        label: "Profit Before Tax",
        field: "netProfitBeforeTax",
        format: "currency",
      },
      { label: "Income Tax", field: "incomeTax", format: "currency" },
      {
        label: "Net Profit After Tax",
        field: "netProfitAfterTax",
        format: "currency",
      },
    ],
  },
  {
    title: "Balance Sheet",
    fields: [
      {
        label: "Cash & Equivalents",
        field: "cashAndEquivalents",
        format: "currency",
      },
      {
        label: "Trade Receivables",
        field: "tradeReceivables",
        format: "currency",
      },
      { label: "Inventory", field: "inventory", format: "currency" },
      {
        label: "Other Current Assets",
        field: "otherCurrentAssets",
        format: "currency",
      },
      {
        label: "Total Current Assets",
        field: "totalCurrentAssets",
        format: "currency",
      },
      { label: "PP&E", field: "propertyPlantEquipment", format: "currency" },
      {
        label: "Intangible Assets",
        field: "intangibleAssets",
        format: "currency",
      },
      {
        label: "Other Non-Current Assets",
        field: "otherNonCurrentAssets",
        format: "currency",
      },
      {
        label: "Total Non-Current Assets",
        field: "totalNonCurrentAssets",
        format: "currency",
      },
      { label: "Total Assets", field: "totalAssets", format: "currency" },
      { label: "Trade Payables", field: "tradePayables", format: "currency" },
      {
        label: "Short-Term Borrowings",
        field: "shortTermBorrowings",
        format: "currency",
      },
      {
        label: "Current Portion LTD",
        field: "currentPortionLongTermDebt",
        format: "currency",
      },
      {
        label: "Other Current Liabilities",
        field: "otherCurrentLiabilities",
        format: "currency",
      },
      {
        label: "Total Current Liabilities",
        field: "totalCurrentLiabilities",
        format: "currency",
      },
      {
        label: "Long-Term Borrowings",
        field: "longTermBorrowings",
        format: "currency",
      },
      {
        label: "Other Non-Current Liabilities",
        field: "otherNonCurrentLiabilities",
        format: "currency",
      },
      {
        label: "Total Non-Current Liabilities",
        field: "totalNonCurrentLiabilities",
        format: "currency",
      },
      {
        label: "Total Liabilities",
        field: "totalLiabilities",
        format: "currency",
      },
      { label: "Net Assets", field: "netAssets", format: "currency" },
      {
        label: "Retained Earnings",
        field: "retainedEarnings",
        format: "currency",
      },
      { label: "Total Equity", field: "totalEquity", format: "currency" },
    ],
  },
  {
    title: "Cash Flow",
    fields: [
      {
        label: "Operating Cash Flow",
        field: "operatingCashFlow",
        format: "currency",
      },
      {
        label: "Investing Cash Flow",
        field: "investingCashFlow",
        format: "currency",
      },
      {
        label: "Financing Cash Flow",
        field: "financingCashFlow",
        format: "currency",
      },
      { label: "Net Cash Change", field: "netCashFlow", format: "currency" },
    ],
  },
  {
    title: "Statutory & Related Party",
    fields: [
      { label: "Tax Liabilities", field: "taxLiabilities", format: "currency" },
      {
        label: "Superannuation",
        field: "superannuationLiabilities",
        format: "currency",
      },
      {
        label: "RP Receivables",
        field: "relatedPartyReceivables",
        format: "currency",
      },
      {
        label: "RP Payables",
        field: "relatedPartyPayables",
        format: "currency",
      },
    ],
  },
  {
    title: "Aged Payables",
    fields: [
      { label: "Current", field: "payablesCurrent", format: "currency" },
      { label: "30 Days", field: "payables30Days", format: "currency" },
      { label: "60 Days", field: "payables60Days", format: "currency" },
      { label: "90+ Days", field: "payables90PlusDays", format: "currency" },
    ],
  },
  {
    title: "Aged Receivables",
    fields: [
      { label: "Current", field: "receivablesCurrent", format: "currency" },
      { label: "30 Days", field: "receivables30Days", format: "currency" },
      { label: "60 Days", field: "receivables60Days", format: "currency" },
      { label: "90+ Days", field: "receivables90PlusDays", format: "currency" },
    ],
  },
  {
    title: "Derived Ratios",
    fields: [
      {
        label: "Current Ratio",
        field: "currentRatio",
        format: "ratio",
        derived: true,
        formula: "totalCurrentAssets / totalCurrentLiabilities",
      },
      {
        label: "Debt Ratio",
        field: "debtRatio",
        format: "ratio",
        derived: true,
        formula: "totalLiabilities / totalAssets",
      },
      {
        label: "Working Capital",
        field: "workingCapital",
        format: "currency",
        derived: true,
        formula: "totalCurrentAssets - totalCurrentLiabilities",
      },
    ],
  },
];

// Build label map for mutations
const FIELD_LABEL_MAP: Record<string, string> = {};
for (const cat of FIELD_CATEGORIES) {
  for (const f of cat.fields) {
    FIELD_LABEL_MAP[f.field] = f.label;
  }
}

// Re-map / allocation targets: every editable (non-derived) numeric field.
const REMAP_TARGET_FIELDS: FieldDef[] = FIELD_CATEGORIES.flatMap((c) =>
  c.fields.filter((f) => !f.derived),
);

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function fmtCurrency(n?: number): string {
  if (n == null) return "\u2014";
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return n < 0 ? `(${formatted})` : formatted;
}

function fmtValue(n: number | undefined, format: string): string {
  if (n == null) return "\u2014";
  if (format === "ratio") return n.toFixed(2);
  if (format === "percentage") return `${(n * 100).toFixed(1)}%`;
  return fmtCurrency(n);
}

function fmtDateDisplay(iso?: string): string {
  if (!iso) return "\u2014";
  const d = parse(iso, "yyyy-MM-dd", new Date());
  return isValid(d) ? fmtDate(d, "d MMM yyyy") : iso;
}

// ---------------------------------------------------------------------------
// Category Card — mirrors the BlockTable style
// ---------------------------------------------------------------------------

function CategoryCard({
  title,
  entry,
  fields,
  onFieldSave,
  onSourcesSave,
  onAdjust,
  onRevertAdjustment,
  activeAdjustments,
  onRemapSource,
  onViewDocument,
  isViewer,
}: {
  title: string;
  entry: FinancialPeriodEntryUI;
  fields: FieldDef[];
  onFieldSave: (field: string, value: FieldSaveValue, label: string) => void;
  onSourcesSave: (
    fieldName: string,
    label: string,
    sources: FieldSourceUI[],
  ) => void;
  onAdjust?: (
    field: string,
    value: number,
    reason: string,
    label: string,
  ) => void;
  onRevertAdjustment?: (adjustmentId: string) => void;
  activeAdjustments?: Map<string, AdjustmentRow>;
  onRemapSource?: (
    fromField: string,
    lineItem: string,
    documentId: string | undefined,
    toField: string,
  ) => void;
  onViewDocument?: (docId: string) => void;
  isViewer?: boolean;
}) {
  const [addingField, setAddingField] = useState(false);

  // Only show fields that have data, sources, or an active adjustment
  const populated = fields.filter(
    (f) =>
      entry[f.field] != null ||
      (entry.fieldSources?.[f.field]?.length ?? 0) > 0 ||
      activeAdjustments?.has(f.field),
  );
  // Hidden fields available to add
  const hiddenFields = fields.filter(
    (f) =>
      entry[f.field] == null &&
      (entry.fieldSources?.[f.field]?.length ?? 0) === 0,
  );

  if (populated.length === 0 && (isViewer || hiddenFields.length === 0))
    return null;

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {/* Header — matches BlockTable */}
      <div className="border-b border-border bg-muted/30 px-4 py-2">
        <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h5>
      </div>
      {/* Rows */}
      <div>
        {populated.map((f) => (
          <FieldRow
            key={f.field}
            def={f}
            entry={entry}
            onSave={onFieldSave}
            onSourcesSave={onSourcesSave}
            onAdjust={onAdjust}
            onRevertAdjustment={onRevertAdjustment}
            activeAdjustment={activeAdjustments?.get(f.field)}
            onRemapSource={onRemapSource}
            onViewDocument={onViewDocument}
            isViewer={isViewer}
          />
        ))}
        {/* Add field */}
        {!isViewer && hiddenFields.length > 0 && (
          <div className="border-t border-border/30 px-4 py-1.5">
            {addingField ? (
              <Select
                onValueChange={(val) => {
                  const def = hiddenFields.find((f) => f.field === val);
                  if (def) onFieldSave(val, 0, def.label);
                  setAddingField(false);
                }}
              >
                <SelectTrigger className="h-6 w-44 text-[11px]">
                  <SelectValue placeholder="Select field..." />
                </SelectTrigger>
                <SelectContent>
                  {hiddenFields.map((f) => (
                    <SelectItem
                      key={f.field}
                      value={f.field}
                      className="text-xs"
                    >
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <button
                type="button"
                onClick={() => setAddingField(true)}
                className="flex items-center gap-1 text-[10px] text-muted-foreground/50 transition-colors hover:text-muted-foreground"
              >
                <Plus className="h-3 w-3" />
                Add field
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field Row — single metric row within a card
// ---------------------------------------------------------------------------

function FieldRow({
  def,
  entry,
  onSave: _onSave,
  onSourcesSave,
  onAdjust,
  onRevertAdjustment,
  activeAdjustment,
  onRemapSource,
  onViewDocument,
  isViewer,
}: {
  def: FieldDef;
  entry: FinancialPeriodEntryUI;
  onSave: (field: string, value: FieldSaveValue, label: string) => void;
  onSourcesSave: (
    fieldName: string,
    label: string,
    sources: FieldSourceUI[],
  ) => void;
  /** Editing a figure records a practitioner adjustment — the source value
   *  is preserved and shown alongside (MOR-810). */
  onAdjust?: (
    field: string,
    value: number,
    reason: string,
    label: string,
  ) => void;
  onRevertAdjustment?: (adjustmentId: string) => void;
  activeAdjustment?: AdjustmentRow;
  onRemapSource?: (
    fromField: string,
    lineItem: string,
    documentId: string | undefined,
    toField: string,
  ) => void;
  onViewDocument?: (docId: string) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftReason, setDraftReason] = useState("");
  const baseValue = entry[def.field] as number | undefined;
  // The row shows the practitioner's figure when an adjustment is active;
  // the extracted original stays visible beneath it.
  const value = activeAdjustment ? activeAdjustment.adjustedValue : baseValue;
  const originalValue = activeAdjustment
    ? (activeAdjustment.originalValue ?? baseValue)
    : undefined;
  const sources: FieldSourceUI[] | undefined = entry.fieldSources?.[def.field];
  const hasSources = sources && sources.length > 0;
  const canEdit = !isViewer && !def.derived && !!onAdjust;
  const isBold = BOLD_FIELDS.has(def.field);
  const isDerived = sources?.every((s) => s.type === "derived");

  const canSave = draft.trim() !== "" && draftReason.trim() !== "";
  const handleSave = () => {
    if (!canSave || !onAdjust) return;
    const parsed = parseFloat(draft);
    if (isNaN(parsed)) return;
    onAdjust(def.field, parsed, draftReason.trim(), def.label);
    setEditing(false);
  };
  const startEditing = () => {
    setDraft(value != null ? String(value) : "");
    setDraftReason(activeAdjustment?.reason ?? "");
    setEditing(true);
  };

  return (
    <div
      className={cn(
        "border-b border-border/30 last:border-b-0",
        isBold && "bg-muted/20",
      )}
    >
      {/* Main row */}
      <div className="group flex items-center justify-between gap-2 px-4 py-2">
        <span
          className={cn(
            "text-xs text-foreground/80",
            isBold && "font-semibold",
          )}
        >
          {def.label}
        </span>
        {!editing && (
          <div className="flex items-center gap-1">
            <span
              className={cn(
                "text-right text-xs tabular-nums",
                isBold && "font-semibold",
                activeAdjustment &&
                  "font-semibold text-amber-700 dark:text-amber-400",
                !activeAdjustment &&
                  value != null &&
                  value < 0 &&
                  "text-red-600 dark:text-red-400",
                value == null && "text-muted-foreground/40",
              )}
            >
              {fmtValue(value, def.format)}
            </span>
            {hasSources &&
              (isDerived ? (
                <Calculator className="h-3 w-3 shrink-0 text-amber-500/60" />
              ) : (
                <Link2 className="h-3 w-3 shrink-0 text-violet-500/60" />
              ))}
            {def.derived && def.formula && (
              <span className="ml-0.5 hidden text-[9px] italic text-muted-foreground/40 group-hover:inline">
                = {def.formula}
              </span>
            )}
            {canEdit && (
              <button
                onClick={startEditing}
                title="Adjust this figure (the extracted value is preserved)"
                className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:!text-muted-foreground group-hover:text-muted-foreground/50"
              >
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>
      {/* Adjustment editor — value + mandatory reason */}
      {editing && (
        <div className="space-y-1.5 px-4 pb-2">
          <div className="flex items-center gap-1">
            <Input
              type="number"
              step="any"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Adjusted value"
              className="h-6 w-36 text-right text-xs tabular-nums"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") setEditing(false);
              }}
              autoFocus
            />
            <Input
              value={draftReason}
              onChange={(e) => setDraftReason(e.target.value)}
              placeholder="Reason (required)"
              className="h-6 flex-1 text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={cn(
                "rounded p-0.5",
                canSave
                  ? "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                  : "cursor-not-allowed text-muted-foreground/30",
              )}
            >
              <Check className="h-3 w-3" />
            </button>
            <button
              onClick={() => setEditing(false)}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Recorded as an adjustment — the extracted figure is preserved and
            this can be reverted at any time.
          </p>
        </div>
      )}
      {/* Active adjustment: the preserved original + reason + revert */}
      {!editing && activeAdjustment && (
        <div className="-mt-0.5 flex items-center justify-between gap-2 px-4 pb-1.5">
          <p className="truncate text-[10px] text-muted-foreground">
            Original {fmtValue(originalValue, def.format)}
            {" · "}
            {activeAdjustment.reason}
            <span className="text-muted-foreground/60">
              {" — "}
              {activeAdjustment.createdBy},{" "}
              {fmtDate(new Date(activeAdjustment.createdAt), "d MMM yyyy")}
            </span>
          </p>
          {!isViewer && onRevertAdjustment && (
            <button
              type="button"
              onClick={() => onRevertAdjustment(activeAdjustment._id)}
              className="shrink-0 text-[10px] text-muted-foreground underline decoration-dotted hover:text-red-600"
              title="Revert to the extracted figure"
            >
              Revert
            </button>
          )}
        </div>
      )}
      {/* Sources breakdown (collapsible) */}
      {sources && sources.length > 0 && (
        <FieldSourcesEditor
          fieldName={def.field}
          fieldLabel={def.label}
          sources={sources}
          entryId={entry._id}
          onSave={onSourcesSave}
          onRemapSource={onRemapSource}
          onViewDocument={onViewDocument}
          isViewer={isViewer}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field Sources Editor
// ---------------------------------------------------------------------------

function FieldSourcesEditor({
  fieldName,
  fieldLabel,
  sources,
  entryId: _entryId,
  onSave,
  onRemapSource,
  onViewDocument,
  isViewer,
}: {
  fieldName: string;
  fieldLabel: string;
  sources: FieldSourceUI[];
  entryId: string;
  onSave: (fieldName: string, label: string, sources: FieldSourceUI[]) => void;
  onRemapSource?: (
    fromField: string,
    lineItem: string,
    documentId: string | undefined,
    toField: string,
  ) => void;
  onViewDocument?: (docId: string) => void;
  isViewer?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [remappingIdx, setRemappingIdx] = useState<number | null>(null);
  const [draftLineItem, setDraftLineItem] = useState("");
  const [draftValue, setDraftValue] = useState("");

  const componentSources = sources.filter((s) => s.type === "component");
  const statedSources = sources.filter((s) => s.type === "stated");
  const derivedSources = sources.filter((s) => s.type === "derived");

  const handleRemoveSource = (idx: number) => {
    onSave(
      fieldName,
      fieldLabel,
      sources.filter((_, i) => i !== idx),
    );
  };

  const handleUpdateSource = (idx: number) => {
    const updated = [...sources];
    updated[idx] = {
      ...updated[idx],
      lineItem: draftLineItem,
      value: draftValue ? parseFloat(draftValue) : undefined,
    };
    onSave(fieldName, fieldLabel, updated);
    setEditingIdx(null);
  };

  const handleAddSource = () => {
    onSave(fieldName, fieldLabel, [
      ...sources,
      { type: "component" as const, lineItem: "New item", value: 0 },
    ]);
  };

  return (
    <div className="px-4 pb-2">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-[10px] text-muted-foreground/50 transition-colors hover:text-muted-foreground"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {sources.length} source{sources.length !== 1 ? "s" : ""}
      </button>

      {expanded && (
        <div className="ml-1 mt-1 space-y-0.5 border-l-2 border-border/30 pl-2">
          {derivedSources.map((s, i) => (
            <div
              key={`d-${i}`}
              className="py-0.5 text-[10px] italic text-muted-foreground"
            >
              = {s.formula}
            </div>
          ))}

          {statedSources.map((s, i) => {
            const { documentId, fileName } = s;
            return (
              <div
                key={`s-${i}`}
                className="flex items-center gap-2 py-0.5 text-[10px]"
              >
                {fileName && onViewDocument && documentId ? (
                  <button
                    type="button"
                    onClick={() => onViewDocument(documentId)}
                    className="inline-flex shrink-0 items-center gap-1 text-violet-600 hover:underline dark:text-violet-400"
                  >
                    <FileText className="h-2.5 w-2.5" />
                    {s.fileName}
                  </button>
                ) : s.fileName ? (
                  <span className="inline-flex shrink-0 items-center gap-1 text-muted-foreground">
                    <FileText className="h-2.5 w-2.5" />
                    {s.fileName}
                  </span>
                ) : null}
                {s.lineItem && (
                  <span className="truncate text-muted-foreground">
                    &ldquo;{s.lineItem}&rdquo;
                  </span>
                )}
              </div>
            );
          })}

          {componentSources.map((s) => {
            const realIdx = sources.indexOf(s);
            if (editingIdx === realIdx) {
              return (
                <div key={realIdx} className="flex items-center gap-1 py-0.5">
                  <Input
                    value={draftLineItem}
                    onChange={(e) => setDraftLineItem(e.target.value)}
                    className="h-5 flex-1 text-[10px]"
                    placeholder="Line item"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdateSource(realIdx);
                      if (e.key === "Escape") setEditingIdx(null);
                    }}
                    autoFocus
                  />
                  <Input
                    type="number"
                    step="any"
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    className="h-5 w-20 text-right text-[10px] tabular-nums"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdateSource(realIdx);
                      if (e.key === "Escape") setEditingIdx(null);
                    }}
                  />
                  <button
                    onClick={() => handleUpdateSource(realIdx)}
                    className="rounded p-0.5 text-emerald-600"
                  >
                    <Check className="h-2.5 w-2.5" />
                  </button>
                  <button
                    onClick={() => setEditingIdx(null)}
                    className="rounded p-0.5 text-muted-foreground"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              );
            }
            if (remappingIdx === realIdx && onRemapSource) {
              return (
                <div key={realIdx} className="flex items-center gap-1 py-0.5">
                  <span className="flex-1 truncate text-[10px] text-muted-foreground">
                    Move &ldquo;{s.lineItem ?? "Unknown"}&rdquo; to:
                  </span>
                  <Select
                    onValueChange={(toField) => {
                      onRemapSource(
                        fieldName,
                        s.lineItem ?? "",
                        s.documentId,
                        toField,
                      );
                      setRemappingIdx(null);
                    }}
                  >
                    <SelectTrigger className="h-5 w-40 shrink-0 text-[10px]">
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {REMAP_TARGET_FIELDS.filter(
                        (f) => f.field !== fieldName,
                      ).map((f) => (
                        <SelectItem
                          key={f.field}
                          value={f.field}
                          className="text-xs"
                        >
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => setRemappingIdx(null)}
                    className="rounded p-0.5 text-muted-foreground"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              );
            }
            return (
              <div
                key={realIdx}
                className="group/src flex items-center justify-between gap-2 py-0.5"
              >
                <span className="truncate text-[10px] text-muted-foreground">
                  {s.lineItem ?? "Unknown"}
                </span>
                <div className="flex items-center gap-0.5">
                  <span className="shrink-0 text-[10px] tabular-nums text-foreground/80">
                    {s.value != null ? fmtCurrency(s.value) : "\u2014"}
                  </span>
                  {!isViewer && (
                    <>
                      {onRemapSource && s.lineItem && (
                        <button
                          onClick={() => setRemappingIdx(realIdx)}
                          title="Move to a different category"
                          className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:!text-muted-foreground group-hover/src:text-muted-foreground/50"
                        >
                          <ArrowRightLeft className="h-2.5 w-2.5" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setDraftLineItem(s.lineItem ?? "");
                          setDraftValue(s.value != null ? String(s.value) : "");
                          setEditingIdx(realIdx);
                        }}
                        className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:!text-muted-foreground group-hover/src:text-muted-foreground/50"
                      >
                        <Pencil className="h-2.5 w-2.5" />
                      </button>
                      <button
                        onClick={() => handleRemoveSource(realIdx)}
                        className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:!text-red-500 group-hover/src:text-red-400/50"
                      >
                        <Trash2 className="h-2.5 w-2.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {/* Doc links for component sources */}
          {(() => {
            const docs = new Map<
              string,
              { documentId: string; fileName: string }
            >();
            for (const s of componentSources) {
              if (s.documentId && s.fileName)
                docs.set(s.documentId, {
                  documentId: s.documentId,
                  fileName: s.fileName,
                });
            }
            return Array.from(docs.values()).map((doc) => (
              <div key={doc.documentId} className="py-0.5">
                {onViewDocument ? (
                  <button
                    type="button"
                    onClick={() => onViewDocument(doc.documentId)}
                    className="inline-flex items-center gap-1 text-[10px] text-violet-600 hover:underline dark:text-violet-400"
                  >
                    <FileText className="h-2.5 w-2.5" />
                    {doc.fileName}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                    <FileText className="h-2.5 w-2.5" />
                    {doc.fileName}
                  </span>
                )}
              </div>
            ));
          })()}

          {!isViewer && (
            <button
              type="button"
              onClick={handleAddSource}
              className="flex items-center gap-1 pt-0.5 text-[10px] text-muted-foreground/50 transition-colors hover:text-muted-foreground"
            >
              <Plus className="h-2.5 w-2.5" />
              Add item
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adjustment history (MOR-810)
// ---------------------------------------------------------------------------

interface AdjustmentRow {
  _id: string;
  entryId: string;
  field: string;
  adjustedValue: number;
  originalValue?: number;
  reason: string;
  status: "active" | "superseded" | "reverted";
  createdBy: string;
  createdAt: number;
  endedBy?: string;
  endedAt?: number;
}

/**
 * Audit trail of every adjustment event on this period, newest first. The
 * editing surface is the field rows themselves (pencil = adjust); this card
 * only records what happened and lets an active adjustment be reverted.
 */
function AdjustmentHistoryCard({
  rows,
  isViewer,
  onRevert,
}: {
  rows: AdjustmentRow[];
  isViewer?: boolean;
  onRevert?: (adjustmentId: string) => void;
}) {
  if (rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => b.createdAt - a.createdAt);
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="border-b border-border bg-muted/30 px-4 py-2">
        <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Adjustment History
        </h5>
      </div>
      <div className="divide-y divide-border/30">
        {sorted.map((a) => (
          <div
            key={a._id}
            className="flex items-start justify-between gap-2 px-4 py-2"
          >
            <div className="min-w-0">
              <p className="text-xs text-foreground/80">
                <span className="font-medium">
                  {FIELD_LABEL_MAP[a.field] ?? a.field}
                </span>
                {": "}
                <span className="tabular-nums text-muted-foreground">
                  {a.originalValue != null
                    ? fmtCurrency(a.originalValue)
                    : "\u2014"}
                </span>
                {" \u2192 "}
                <span className="font-medium tabular-nums">
                  {fmtCurrency(a.adjustedValue)}
                </span>
              </p>
              <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                {a.reason}
                <span className="text-muted-foreground/60">
                  {" \u2014 "}
                  {a.createdBy}, {fmtDate(new Date(a.createdAt), "d MMM yyyy")}
                </span>
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {a.status !== "active" && (
                <Badge
                  variant="outline"
                  className="text-[9px] capitalize text-muted-foreground"
                >
                  {a.status}
                </Badge>
              )}
              {a.status === "active" && !isViewer && onRevert && (
                <button
                  type="button"
                  onClick={() => onRevert(a._id)}
                  className="text-[10px] text-muted-foreground underline decoration-dotted hover:text-red-600"
                >
                  Revert
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

interface FinancialPeriodDetailPanelProps {
  entry: FinancialPeriodEntryUI | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  onViewDocument?: (docId: string) => void;
  isViewer?: boolean;
}

export function FinancialPeriodDetailPanel({
  entry,
  open,
  onClose,
  onDeleted,
  onViewDocument,
  isViewer,
}: FinancialPeriodDetailPanelProps) {
  const [visible, setVisible] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open) requestAnimationFrame(() => setVisible(true));
    else {
      setVisible(false);
      setConfirmDelete(false);
    }
  }, [open]);

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .updateFinancialPeriodField,
    ),
  });
  const updateFieldSources = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .updateFinancialPeriodFieldSources,
    ),
  });
  const deletePeriod = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .deleteFinancialPeriod,
    ),
  });
  const remapSource = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .remapFieldSource,
    ),
  });
  const allocateItem = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .allocateUnallocatedItem,
    ),
  });
  const setAdjustment = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .setAdjustment,
    ),
  });
  const revertAdjustment = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.financialAnalysis.publicMutations
        .revertAdjustment,
    ),
  });

  const entryJobId = entry?.jobId as Id<"jobs"> | undefined;
  // `convexQuery` + `enabled: false` still opens the subscription — the
  // ConvexQueryClient subscribes on the query cache's "added" event, which
  // fires for disabled queries too, so the server saw `{ jobId: undefined }`
  // and rejected it. Convex's own hook understands "skip".
  const allAdjustments = useConvexQuery(
    api.jobs.summarization.publicQueries.listFinancialAdjustmentsForJob,
    entryJobId ? { jobId: entryJobId } : "skip",
  );
  const entryAdjustments = useMemo(
    () => (allAdjustments ?? []).filter((a) => a.entryId === entry?._id),
    [allAdjustments, entry?._id],
  );
  const activeAdjustmentsByField = useMemo(() => {
    const map = new Map<string, AdjustmentRow>();
    for (const a of entryAdjustments) {
      if (a.status === "active") map.set(a.field, a);
    }
    return map;
  }, [entryAdjustments]);

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!entry?.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of entry.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [entry?.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  if (!open || !entry) return null;

  const handleFieldSave = (
    field: string,
    value: FieldSaveValue,
    label: string,
  ) => {
    updateField.mutate({
      entryId: entry._id as Id<"financialPeriodEntries">,
      field,
      value,
      fieldLabel: label,
    });
  };

  const handleSourcesSave = (
    fieldName: string,
    label: string,
    sources: FieldSourceUI[],
  ) => {
    updateFieldSources.mutate({
      entryId: entry._id as Id<"financialPeriodEntries">,
      fieldName,
      fieldLabel: label,
      sources,
    });
  };

  const handleRemapSource = (
    fromField: string,
    lineItem: string,
    documentId: string | undefined,
    toField: string,
  ) => {
    remapSource.mutate({
      entryId: entry._id as Id<"financialPeriodEntries">,
      fromField,
      toField,
      lineItem,
      documentId,
    });
  };

  const handleAllocate = (
    accountName: string,
    documentId: string | undefined,
    toField: string,
  ) => {
    allocateItem.mutate({
      entryId: entry._id as Id<"financialPeriodEntries">,
      accountName,
      documentId,
      toField,
    });
  };

  const handleAdjust = (
    field: string,
    value: number,
    reason: string,
    _label: string,
  ) => {
    setAdjustment.mutate({
      entryId: entry._id as Id<"financialPeriodEntries">,
      field,
      adjustedValue: value,
      reason,
    });
  };

  const handleRevertAdjustment = (adjustmentId: string) => {
    revertAdjustment.mutate({
      adjustmentId: adjustmentId as Id<"financialAdjustments">,
    });
  };

  const handleDelete = () => {
    deletePeriod.mutate(
      { entryId: entry._id as Id<"financialPeriodEntries"> },
      { onSuccess: () => onDeleted?.() },
    );
  };

  const canEdit = !isViewer;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/20 transition-opacity duration-200 dark:bg-black/40",
          visible ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          "fixed right-0 top-0 z-50 flex h-full w-full max-w-lg flex-col border-l bg-background shadow-xl transition-transform duration-200 ease-out",
          visible ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center gap-3 border-b px-5 pb-3 pt-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-lg font-semibold">
                {displayPeriodLabel(entry)}
              </h3>
              <Badge
                variant="outline"
                className="shrink-0 text-[10px] capitalize"
              >
                {entry.periodType}
              </Badge>
              {entry.isManualOverride && (
                <Badge variant="secondary" className="shrink-0 text-[10px]">
                  Edited
                </Badge>
              )}
            </div>
            {(entry.startDate || entry.endDate) && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {fmtDateDisplay(entry.startDate)} —{" "}
                {fmtDateDisplay(entry.endDate)}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {canEdit && (
              <button
                onClick={() => setConfirmDelete(true)}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                title="Delete period"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
            <p className="text-sm text-red-700 dark:text-red-300">
              Remove this period? This can be undone by re-running the agent.
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                disabled={deletePeriod.isPending}
              >
                Remove
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 space-y-4 overflow-y-auto px-5 pb-8 pt-4">
          {/* Metadata card */}
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="border-b border-border bg-muted/30 px-4 py-2">
              <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Period Metadata
              </h5>
            </div>
            <div className="divide-y divide-border/30">
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-xs text-foreground/80">Type</span>
                {canEdit ? (
                  <Select
                    value={entry.periodType}
                    onValueChange={(val) =>
                      handleFieldSave("periodType", val, "Type")
                    }
                  >
                    <SelectTrigger className="h-6 w-28 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs capitalize text-foreground">
                    {entry.periodType}
                  </span>
                )}
              </div>
              <MetadataRow
                label="Period Label"
                value={entry.periodLabel}
                field="periodLabel"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-xs text-foreground/80">Start Date</span>
                {canEdit ? (
                  <DatePicker
                    value={entry.startDate}
                    onChange={(val) =>
                      handleFieldSave(
                        "startDate",
                        val || undefined,
                        "Start Date",
                      )
                    }
                    className="w-36"
                  />
                ) : (
                  <span className="text-xs text-foreground">
                    {fmtDateDisplay(entry.startDate)}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-xs text-foreground/80">End Date</span>
                {canEdit ? (
                  <DatePicker
                    value={entry.endDate}
                    onChange={(val) =>
                      handleFieldSave("endDate", val || undefined, "End Date")
                    }
                    className="w-36"
                  />
                ) : (
                  <span className="text-xs text-foreground">
                    {fmtDateDisplay(entry.endDate)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Unallocated line items — surfaced instead of silently bucketed
              (MOR-857). Allocating moves the value into the chosen field with
              full provenance and recomputes the ratios/indicators. */}
          {(entry.unallocatedItems?.length ?? 0) > 0 && (
            <div className="overflow-hidden rounded-lg border border-amber-200 dark:border-amber-900">
              <div className="border-b border-amber-200 bg-amber-50/60 px-4 py-2 dark:border-amber-900 dark:bg-amber-950/20">
                <h5 className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                  Unallocated Line Items
                </h5>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  Not included in any category figure until allocated.
                </p>
              </div>
              <div className="divide-y divide-border/30">
                {(entry.unallocatedItems ?? []).map((u) => (
                  <div
                    key={`${u.documentId ?? ""}|${u.accountName}`}
                    className="flex items-center justify-between gap-2 px-4 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs text-foreground/80">
                        {u.accountName}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {fmtCurrency(u.balance)}
                        {u.side && u.side !== "other" ? ` · ${u.side}` : ""}
                        {u.fileName ? ` · ${u.fileName}` : ""}
                      </p>
                    </div>
                    {!isViewer && (
                      <Select
                        onValueChange={(toField) =>
                          handleAllocate(u.accountName, u.documentId, toField)
                        }
                      >
                        <SelectTrigger className="h-6 w-44 shrink-0 text-[11px]">
                          <SelectValue placeholder="Allocate to..." />
                        </SelectTrigger>
                        <SelectContent>
                          {REMAP_TARGET_FIELDS.map((f) => (
                            <SelectItem
                              key={f.field}
                              value={f.field}
                              className="text-xs"
                            >
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial categories — the pencil on a row records a
              practitioner adjustment (MOR-810): the adjusted figure shows on
              the row, the extracted original is preserved beneath it. */}
          {FIELD_CATEGORIES.map((cat) => (
            <CategoryCard
              key={cat.title}
              title={cat.title}
              entry={entry}
              fields={cat.fields}
              onFieldSave={handleFieldSave}
              onSourcesSave={handleSourcesSave}
              onAdjust={handleAdjust}
              onRevertAdjustment={handleRevertAdjustment}
              activeAdjustments={activeAdjustmentsByField}
              onRemapSource={handleRemapSource}
              onViewDocument={onViewDocument}
              isViewer={isViewer}
            />
          ))}

          {/* Adjustment audit trail (MOR-810) */}
          <AdjustmentHistoryCard
            rows={entryAdjustments}
            isViewer={isViewer}
            onRevert={handleRevertAdjustment}
          />

          {/* Source Documents */}
          {entry.sourceDocuments && entry.sourceDocuments.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="border-b border-border bg-muted/30 px-4 py-2">
                <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Source Documents
                </h5>
              </div>
              <div className="flex flex-wrap gap-1.5 px-4 py-2">
                {entry.sourceDocuments.map((doc) =>
                  onViewDocument ? (
                    <button
                      key={doc.documentId}
                      type="button"
                      onClick={() => onViewDocument(doc.documentId)}
                      className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 transition-colors hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300 dark:hover:bg-violet-900"
                    >
                      <FileText className="h-3 w-3 shrink-0" />
                      {doc.fileName}
                    </button>
                  ) : (
                    <span
                      key={doc.documentId}
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"
                    >
                      <FileText className="h-3 w-3" />
                      {doc.fileName}
                    </span>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Change History */}
          {entry.changeHistory && entry.changeHistory.length > 0 && (
            <div className="mt-2">
              <ChangeHistory
                history={entry.changeHistory}
                usersById={historyUsersById}
                formatters={FINANCIAL_PERIOD_FIELD_FORMATTERS}
                fieldLabels={FINANCIAL_PERIOD_FIELD_LABELS}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Metadata Row (text editable — for period label)
// ---------------------------------------------------------------------------

function MetadataRow({
  label,
  value,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value?: string | null;
  field: string;
  onSave: (field: string, value: FieldSaveValue, label: string) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const handleSave = () => {
    onSave(field, draft || undefined, label);
    setEditing(false);
  };

  return (
    <div className="group flex items-center justify-between px-4 py-2">
      <span className="text-xs text-foreground/80">{label}</span>
      {editing ? (
        <div className="flex items-center gap-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-6 w-32 text-xs"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") setEditing(false);
            }}
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded p-0.5 text-emerald-600"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded p-0.5 text-muted-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <span className="text-xs text-foreground">{value ?? "\u2014"}</span>
          {!isViewer && (
            <button
              onClick={() => {
                setDraft(value ?? "");
                setEditing(true);
              }}
              className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:!text-muted-foreground group-hover:text-muted-foreground/50"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
